import type { Translations } from "./index";

export const en: Translations = {
  // ── EditorLayout ──────────────────────────────────────────
  "preview.missingQuestion": "A valid questionId parameter is required for preview.",
  "preview.loading": "Loading question...",
  "toast.questionLoaded": "Question loaded",
  "toast.questionLoadFailed": "Failed to load question. Please try again.",
  "toast.templateLoading": "Loading template...",
  "toast.templateDataNotFound": "Template data could not be retrieved",
  "toast.templateLoaded": "Template loaded",
  "toast.templateLoadError": "An error occurred while loading the template",
  "toast.draftCreated": "New draft created!",
  "toast.draftUpdated": "Draft updated successfully!",
  "toast.draftSaved": "Draft saved successfully!",
  "toast.draftSaveFailed": "Failed to save draft",
  "toast.draftSaveError": "Failed to save draft. Please try again.",
  "toast.templateCreated": "New template created!",
  "toast.templateUpdated": "Template updated successfully!",
  "toast.templateSaved": "Template saved successfully!",
  "toast.templateSaveFailed": "Failed to save template",
  "toast.templateSaveError": "Failed to save template. Please try again.",
  "toast.normalSaveSettingsRequired": "Complete question settings before saving.",
  "toast.questionCreated": "New question created!",
  "toast.questionUpdated": "Question updated successfully!",
  "toast.questionSaved": "Question saved successfully!",
  "toast.questionSaveFailed": "Failed to save question",
  "toast.questionSaveError": "Failed to save question. Please try again.",
  "toast.canvasCleared": "Canvas cleared",
  "toast.shortcutAdded": "added to toolbar",
  "toast.shortcutRemoved": "removed from toolbar",
  "confirm.clearCanvas": "Are you sure you want to delete all elements? This action cannot be undone.",
  "overlay.redirecting": "Redirecting...",
  "overlay.loading": "Loading...",
  "header.title": "Question Editor",
  "header.clearCanvas": "Clear Canvas",
  "header.canvasSettings": "Canvas Settings",
  "header.questionSettings": "Question Settings",
  "header.answerKey": "Answer Key",
  "header.preview": "Preview",
  "header.save": "Save",
  "header.questionLoading": "Loading question...",
  "panel.questionType.title": "Question Type",
  "panel.questionType.loading": "Loading question types...",
  "panel.questionType.empty": "No question types found.",
  "panel.questionType.selectedFallback": "Selected Type",
  "panel.questionType.unnamed": "Unnamed Type",
  "panel.elements.title": "Elements",
  "panel.elements.noTypeHelp": "Select a question type to enable form elements. Basic elements are always available.",
  "panel.properties.title": "Element Properties",
  "panel.templates.button": "📄 Templates",
  "save.draft.createTitle": "Save as Draft",
  "save.draft.createSubtitle": "Save incomplete questions",
  "save.draft.updateTitle": "Update Draft",
  "save.draft.updateSubtitle": "Update existing draft",
  "save.draft.duplicateTitle": "Save as New Draft",
  "save.draft.duplicateSubtitle": "Save this version as a new draft",
  "save.template.createTitle": "Save as Template",
  "save.template.createSubtitle": "Create a reusable template",
  "save.template.updateTitle": "Update Template",
  "save.template.updateSubtitle": "Update existing template",
  "save.template.duplicateTitle": "Save as New Template",
  "save.template.duplicateSubtitle": "Save this layout as a new template",
  "save.normal.createTitle": "Save",
  "save.normal.createSubtitle": "Save the question ready for publishing",
  "save.normal.createSubtitleDisabled": "Complete question settings to save",
  "save.normal.updateTitle": "Update Question",
  "save.normal.updateSubtitle": "Update the published question",
  "save.normal.duplicateTitle": "Save as New Question",
  "save.normal.duplicateSubtitle": "Create a new question from this work",
  "save.updateDisabledHint": "Make changes before updating",
  "save.duplicateDisabledHint": "Make changes before creating a new question",
  "contextMenu.style": "Style",
  "contextMenu.bold": "Bold",
  "contextMenu.italic": "Italic",
  "contextMenu.underline": "Underline",
  "contextMenu.openInEditor": "Open in Editor",
  "contextMenu.textToSpeech": "Convert to Speech",
  "contextMenu.permanentDelete": "Permanently Delete",
  "contextMenu.placeBehindText": "Place behind text (play on hover)",
  "contextMenu.placeBehindTextDone": "Audio placed behind the text. It will play on hover in preview.",
  "contextMenu.placeBehindTextMissing": "The source text for this audio could not be found.",
  "contextMenu.duplicate": "Duplicate",
  "contextMenu.delete": "Delete",
  "tts.title": "Convert Text to Speech",
  "tts.subtitle": "The selected text block will be converted to audio with ElevenLabs.",
  "tts.text": "Text",
  "tts.voiceId": "Voice",
  "tts.voiceIdPlaceholder": "ElevenLabs voice_id",
  "tts.selectVoice": "Select a voice",
  "tts.name": "File Name",
  "tts.namePlaceholder": "Example: question-voiceover",
  "tts.modelId": "Model",
  "tts.languageCode": "Language",
  "tts.outputFormat": "Output Format",
  "tts.outputFormatPlaceholder": "Use backend default",
  "tts.speed": "Speech Speed",
  "tts.cancel": "Cancel",
  "tts.generate": "Generate Audio",
  "tts.generating": "Generating...",
  "tts.success": "Audio file was created and added to the canvas.",
  "tts.error": "Audio file could not be created.",
  "tts.emptyTextError": "No text found to convert.",
  "tts.missingUrlError": "Backend did not return an audio file URL.",
  "tts.generatedAudioLabel": "Voiceover",
  "tts.deleteConfirm": "Are you sure you want to permanently delete this audio file from the media library?",
  "tts.deleteSuccess": "Audio file was permanently deleted.",
  "tts.deleteError": "Audio file could not be deleted.",
  "tts.deleteMissingPathError": "Could not find the audio file path to delete.",

  // ── Toolbar ───────────────────────────────────────────────
  "toolbar.edit": "Edit",
  "toolbar.undo": "Undo (Ctrl/Cmd+Z)",
  "toolbar.redo": "Redo (Ctrl/Cmd+Shift+Z)",
  "toolbar.grouping": "Grouping",
  "toolbar.group": "Group",
  "toolbar.ungroup": "Ungroup",
  "toolbar.alignment": "Alignment",
  "toolbar.alignLeft": "Align left",
  "toolbar.alignCenterH": "Center horizontally",
  "toolbar.alignRight": "Align right",
  "toolbar.alignTop": "Align top",
  "toolbar.alignCenterV": "Center vertically",
  "toolbar.alignBottom": "Align bottom",
  "toolbar.alignToCanvas": "Align to Canvas",
  "toolbar.alignCanvasLeft": "Align to canvas left",
  "toolbar.alignCanvasCenterH": "Center horizontally on canvas",
  "toolbar.alignCanvasRight": "Align to canvas right",
  "toolbar.alignCanvasTop": "Align to canvas top",
  "toolbar.alignCanvasCenterV": "Center vertically on canvas",
  "toolbar.alignCanvasBottom": "Align to canvas bottom",
  "toolbar.guides": "Guides",
  "toolbar.toggleRulers": "Show or hide rulers",
  "toolbar.toggleGuides": "Show or hide guides",
  "toolbar.addVerticalGuide": "Add vertical guide",
  "toolbar.addHorizontalGuide": "Add horizontal guide",
  "toolbar.clearGuides": "Clear guides",
  "toolbar.guidePresets": "Presets",
  "toolbar.guidePresetPlaceholder": "Apply preset",
  "toolbar.guidePresetMargin50": "50 px margins",
  "toolbar.guidePresetCenter": "Center axes",
  "toolbar.guidePresetColumns3": "Three columns",
  "toolbar.guidePresetRows3": "Three rows",
  "guides.addVerticalFromRuler": "Add vertical guide from ruler",
  "guides.addHorizontalFromRuler": "Add horizontal guide from ruler",
  "guides.moveOrRemove": "Move guide. Double-click or press Delete to remove.",

  // ── Properties Panel ──────────────────────────────────────
  "props.noElementSelected": "Properties will appear here when an element is selected.",
  "props.cellElementIndicatorTitle": "Cell Element",
  "props.cellElementIndicatorDesc": "You are editing the properties of an element inside a table",
  "props.layers": "Layers",
  "props.layerMoveUp": "Move one layer up",
  "props.layerMoveDown": "Move one layer down",
  "props.general": "General",
  "props.generalSettings": "General Settings",
  "props.elementId": "Element ID",
  "props.type": "Type",
  "props.positionPx": "Position (px)",
  "props.zIndex": "Layer (Z-Index)",
  "props.rotation": "Rotation (°)",
  "props.appearanceTiming": "Appearance Timing",
  "props.appearOrder": "Order (blank = visible immediately)",
  "props.appearOrderPlaceholder": "blank = immediate",
  "props.appearDelaySeconds": "Delay after previous element (sec)",
  "props.minDisplaySeconds": "Wait before next element (sec)",
  "props.transparent": "Transparent",
  "props.color": "Color",
  "props.background": "Background",
  "props.backgroundColor": "Background Color",
  "props.border": "Border",
  "props.borderWidth": "Border Width",
  "props.borderColor": "Border Color",
  "props.borderRadius": "Corner Radius",
  "props.borderStyle": "Border Style",
  "props.thickness": "Thickness",
  "props.corner": "Corner",
  "props.padding": "Padding",
  "props.paddingX": "Padding X",
  "props.paddingY": "Padding Y",
  "props.horizontalPadding": "Horizontal Padding",
  "props.verticalPadding": "Vertical Padding",
  "props.horizontalSpacing": "Horizontal Spacing",
  "props.verticalSpacing": "Vertical Spacing",
  "props.horizontalMargin": "Horizontal Margin",
  "props.verticalMargin": "Vertical Margin",
  "props.fontFamily": "Font Family",
  "props.fontSize": "Font Size",
  "props.size": "Size",
  "props.width": "Width",
  "props.height": "Height",
  "props.opacity": "Opacity",
  "props.alignment": "Alignment",
  "props.textAlign": "Text Alignment",
  "props.left": "Left",
  "props.center": "Center",
  "props.right": "Right",
  "props.justify": "Justify",
  "props.default": "Default",
  "props.label": "Label",
  "props.textColor": "Text Color",
  "props.textColorHover": "Text Color (Hover)",
  "props.textColorActive": "Text Color (Active)",
  "props.image": "Image",
  "props.visual": "Visual",
  "props.none": "None",
  "props.textSettings": "Text Settings",
  "props.textContent": "Text Content",
  "props.style": "Style",
  "props.bold": "Bold",
  "props.italic": "Italic",
  "props.underline": "Underline",
  "props.lineHeight": "Line Height",
  "props.letterSpacing": "Letter Spacing",
  "props.textTransform": "Text Transform",
  "props.shadow": "Shadow",
  "props.borderStyleSolid": "Solid",
  "props.borderStyleDashed": "Dashed",
  "props.borderStyleDotted": "Dotted",
  "props.borderStyleFull": "Full Border",
  "props.borderStyleBottom": "Bottom Border",
  "props.fillInTheBlanksSettings": "Fill in the Blanks Settings",
  "props.fillInTheBlanksContent": "Content (Write answers inside [square brackets])",
  "props.fillInTheBlanksPlaceholder": "Example: The capital of Turkey is [Ankara].",
  "props.hideCorrectAnswers": "Hide Correct Answers",
  "props.hideCorrectAnswersHelp": "When enabled, correct answers (values in square brackets) are hidden on the canvas.",
  "props.blankChoicesDropdown": "Blank Choices (Dropdown)",
  "props.blankChoicesHelp": "If you provide choices for each blank, students will select from a dropdown menu. Otherwise, it will be a text input.",
  "props.dropdownActive": "Dropdown Active",
  "props.addNewChoice": "Add new choice...",
  "props.applyToAllBlanks": "Apply to all blanks",
  "props.boxBackground": "Box Background",
  "props.boxBorder": "Box Border",
  "props.boxTextColor": "Box Text Color",
  "props.text": "Text",
  "props.blankDefaultWidth": "Blank Default Width (px)",
  "props.blankFontFamily": "Blank Font Family",
  "props.blankFontSize": "Blank Font Size",
  "props.blankFontStyles": "Blank Font Styles",
  "props.blankLetterSpacing": "Blank Letter Spacing",
  "props.imageSettings": "Image Settings",
  "props.imageClickToChange": "Click to change image",
  "props.imageSelectFromLibrary": "Select image from media library",
  "props.imageSelected": "Image Selected (Change)",
  "props.selectFromMediaLibrary": "Select from Media Library",
  "props.viewCurrentImage": "View current image",
  "props.altText": "Alt Text",
  "props.objectFit": "Fit Mode",
  "props.objectFitContain": "Contain",
  "props.objectFitCover": "Cover",
  "props.objectFitFill": "Fill",
  "props.objectFitNone": "Original Size",
  "props.objectFitScaleDown": "Scale Down",
  "props.imagePosition": "Image Position",
  "props.posCenter": "Center",
  "props.posTop": "Top",
  "props.posBottom": "Bottom",
  "props.posTopLeft": "Top Left",
  "props.posTopRight": "Top Right",
  "props.posBottomLeft": "Bottom Left",
  "props.posBottomRight": "Bottom Right",
  "props.posTopCenter": "Top Center",
  "props.posRightCenter": "Right Center",
  "props.autoSizeHelp": "Auto-sizing is used when left empty.",
  "props.imageScaling": "Image Scaling",
  "props.fitting": "Fitting",
  "props.audioSettings": "Audio Settings",
  "props.audioUrl": "Audio URL",
  "props.audioClickToChange": "Click to change audio file",
  "props.audioSelectFromLibrary": "Select audio file from sound library",
  "props.audioSelected": "Audio File Selected (Change)",
  "props.audioSelectFromSoundLibrary": "Select from Sound Library",
  "props.audioUrlHelp": "Enter a valid .mp3, .wav, or supported audio file URL.",
  "props.showIcon": "Show icon",
  "props.showLabel": "Show label",
  "props.visibleLabel": "Visible Label",
  "props.audioPlayPlaceholder": "Play audio file",
  "props.autoPlay": "Auto-play on page load (preview)",
  "props.loop": "Loop playback",
  "props.showControls": "Show browser controls",
  "props.iconColor": "Icon Color",
  "props.videoSettings": "Video Settings",
  "props.videoClickToChange": "Click to change video file",
  "props.videoSelectFromLibrary": "Select video from media library",
  "props.videoSelected": "Video Selected (Change)",
  "props.options": "Options",
  "props.multipleSelection": "Multiple Selection",
  "props.deleteOption": "Delete option",
  "props.contentType": "Content Type",
  "props.contentTypeText": "Text",
  "props.contentTypeImage": "Image",
  "props.contentTypeMath": "Math (MathType)",
  "props.optionTextPlaceholder": "Option text...",
  "props.addOption": "+ Add Option",
  "props.layout": "Layout",
  "props.layoutDirection": "Direction",
  "props.layoutVertical": "Vertical",
  "props.layoutHorizontal": "Horizontal",
  "props.gridColumns": "Grid Columns",
  "props.optionSpacing": "Option Spacing",
  "props.uniformWidth": "Uniform Width",
  "props.uniformWidthLabel": "Make Options Equal Width",
  "props.optionPrefix": "Option Prefix",
  "props.textStyle": "Text Style",
  "props.fontWeight": "Font Weight",
  "props.fontWeightThin": "Thin",
  "props.fontWeightNormal": "Normal",
  "props.fontWeightMedium": "Medium",
  "props.fontWeightBold": "Bold",
  "props.fontWeightSemiBold": "Semi Bold",
  "props.fontWeightExtraBold": "Extra Bold",
  "props.colors": "Colors",
  "props.backgroundHover": "Background (Hover)",
  "props.backgroundActive": "Background (Active)",
  "props.borderAndSpacing": "Border & Spacing",
  "props.borderColorHover": "Border Color (Hover)",
  "props.borderColorActive": "Border Color (Active)",
  "props.indicator": "Indicator",
  "props.showIndicator": "Show Indicator",
  "props.indicatorColor": "Indicator Color",
  "props.indicatorColorActive": "Indicator Color (Active)",
  "props.icons": "Icons",
  "props.showIcons": "Show Icons",
  "props.position": "Position",
  "props.iconSize": "Icon Size",
  "props.advancedSettings": "Advanced Settings",
  "props.valueId": "Value (ID)",
  "props.iconUrl": "Icon URL",
  "props.iconUrlChecked": "Icon URL (Checked)",
  "props.iconUrlNormal": "Icon URL (Normal)",
  "props.disabled": "Disabled",
  "props.mathmlFormula": "MathML Formula",
  "props.openEditor": "Open Editor",
  "props.contentAlignment": "Content Alignment",
  "props.horizontal": "Horizontal",
  "props.vertical": "Vertical",
  "props.dragSourceSettings": "Draggable Element Settings",
  "props.elementLabel": "Element",
  "props.idReadOnly": "ID (Read Only)",
  "props.appearanceSettings": "Appearance Settings",
  "props.visualContent": "Visual Content",
  "props.behaviorSettings": "Behavior Settings",
  "props.maxCopyCount": "Maximum copy count",
  "props.numberBackground": "Number Background",
  "props.topLeft": "Top Left",
  "props.topCenter": "Top Center",
  "props.topRight": "Top Right",
  "props.bottomRight": "Bottom Right",
  "props.dropTargetSettings": "Drop Target Settings",
  "props.acceptedElements": "Accepted Elements",
  "props.noElementAccepted": "No elements selected - all elements are accepted",
  "props.labelSettings": "Label Settings",
  "props.labelTextColor": "Label Text Color",
  "props.labelColor": "Label Color",
  "props.labelBackground": "Label Background",
  "props.altTextOptional": "Alt Text (optional)",
  "props.backgroundImageLabel": "Background Image",
  "props.blankLabel": "Blank",
  "props.cloneable": "Cloneable (unlimited copies)",
  "props.colorActive": "Active Color",
  "props.dropAreaCoordinatesHint": "Drop area position and size",
  "props.dropTargetPlaceholderText": "Placeholder Text",
  "props.emptyMessageHint": "Message shown when the area is empty",
  "props.highlightColor": "Highlight Color",
  "props.highlightOnDrag": "Highlight on Drag",
  "props.nameShownInAnswerKey": "This name is shown in the answer key",
  "props.option": "Option",
  "props.restrictDropArea": "Restrict Drop Area",
  "props.showAsImage": "Show Visual Content",
  "props.showAsShape": "Show Visual Content as Geometric Shape",
  "props.showDropAreaOutline": "Show Drop Area Outline",
  "props.showEmptyMessage": "Show Empty Message",
  "props.showLess": "Show Less",
  "props.showNumberingInTarget": "Show Numbering in Target",
  "props.sourceColor": "Source Color",
  "props.targetColor": "Target Color",
  "props.unlimitedCopiesHint": "Item can be dragged an unlimited number of times",
  "props.videoUrlHelp": "Paste the video link (URL)",
  "props.labelHorizontalPadding": "Label Horizontal Padding",
  "props.labelVerticalPadding": "Label Vertical Padding",
  "props.labelCornerRadius": "Label Corner Radius",
  "props.contentLayout": "Content Layout",
  "props.contentPlacement": "Content Placement",
  "props.start": "Start",
  "props.itemSpacing": "Item Spacing",
  "props.enableBackgroundImage": "Enable background image",
  "props.dropArea": "Drop Area",
  "props.frameColor": "Frame Color",
  "props.frameWidth": "Frame Width",
  "props.orderListSettings": "Order List Settings",
  "props.orderListArea": "Order List Area",
  "props.elements": "Elements",
  "props.elementLabelPlaceholder": "Element label",
  "props.deleteElement": "Delete Element",
  "props.labelRounding": "Label Rounding",
  "props.labelPaddingH": "Label Padding (H)",
  "props.labelPaddingV": "Label Padding (V)",
  "props.borderRoundness": "Border Roundness",
  "props.paddingH": "Padding (H)",
  "props.paddingV": "Padding (V)",
  "props.itemGap": "Item Gap",
  "props.itemBackgroundColor": "Item Background Color",
  "props.itemBorderColor": "Item Border Color",
  "props.itemTextColor": "Item Text Color",
  "props.itemFontSize": "Item Font Size",
  "props.itemBorderWidth": "Item Border Width",
  "props.itemBorderRadius": "Item Border Radius",
  "props.itemPaddingH": "Item Padding (H)",
  "props.itemPaddingV": "Item Padding (V)",
  "props.matchingPointSettings": "Matching Point Settings",
  "props.name": "Name",
  "props.sourcePointName": "Source Point Name",
  "props.targetPointName": "Target Point Name",
  "props.pointSettings": "Point Settings",
  "props.borderThickness": "Border Thickness",
  "props.sourceBorderColor": "Source Border Color",
  "props.connectionColor": "Connection Color",
  "props.connectionWidth": "Connection Width",
  "props.connectionStyle": "Connection Style",
  "props.targetBorderColor": "Target Border Color",
  "props.paintAreaSettings": "Paint Area Settings",
  "props.shapeName": "Shape Name",
  "props.shapeNamePlaceholder": "e.g. Circle A",
  "props.shapeType": "Shape Type",
  "props.star": "Star",
  "props.triangle": "Triangle",
  "props.pentagon": "Pentagon",
  "props.hexagon": "Hexagon",
  "props.flower": "Flower",
  "props.sun": "Sun",
  "props.paintSwatchSettings": "Color Swatch Settings",
  "props.pushToTalkSettings": "Push to Talk Settings",
  "props.buttonDimensions": "Button Dimensions",
  "props.buttonSizePx": "Button (px)",
  "props.contentSizePx": "Content (px)",
  "props.textSizePx": "Text (px)",
  "props.canvasStatus": "Canvas Status",
  "props.idle": "Idle",
  "props.listening": "Listening",
  "props.error": "Error",
  "props.canvasStatusHelp": "This setting only changes the canvas appearance during editing.",
  "props.micButton": "Microphone Button",
  "props.errorAlert": "Error Alert",
  "props.textArea": "Text Area",
  "props.textAreaTitle": "Text Area Title",
  "props.errorMessagePreview": "Error Message (Preview)",
  "props.errorMessagePlaceholder": "e.g. Microphone permission denied.",
  "props.textAreaLabel": "Text Area Label",
  "props.textAreaLabelPlaceholder": "Recorded Answer",
  "props.textAreaPlaceholder": "Text Area Placeholder",
  "props.errorBackground": "Error Background",
  "props.errorBorder": "Error Border",
  "props.errorText": "Error Text",
  "props.buttonShape": "Button Shape",
  "props.shape": "Shape",
  "props.shapeCircle": "Circle",
  "props.shapeSquare": "Square",
  "props.roundness": "Roundness",
  "props.buttonColors": "Button Colors",
  "props.waiting": "Waiting",
  "props.listeningColor": "Listening",
  "props.buttonTextColors": "Button Text Colors",
  "props.waveColor": "Wave Color",
  "props.buttonContent": "Button Content",
  "props.contentTypeLabel": "Type",
  "props.iconDefault": "Icon (default)",
  "props.visualMedia": "Visual (Media)",
  "props.idleButtonText": "Text displayed on the button",
  "props.selectVisual": "Select Visual",
  "props.remove": "Remove",
  "props.preview": "Preview",
  "props.empty": "Empty",
  "props.clearAll": "Clear All",
  "props.errorPlaceholderHelp": "This setting is now edited from the \"Text Area Placeholder\" field above when Canvas Status = Error is selected.",
  "props.errorPlaceholderTitle": "Text area placeholder in error state",
  "props.mathSettings": "Math Settings",
  "props.tableSettings": "Table Settings",
  "props.row": "Row",
  "props.column": "Column",
  "props.spacings": "Spacings (px)",
  "props.cellPadding": "Cell Padding",
  "props.cellBackgroundColor": "Cell Background Color",
  "props.timerSettings": "Timer Settings",
  "props.durationSeconds": "Duration (Seconds)",
  "props.displayFormat": "Display Format",
  "props.formatMinSec": "Minutes:Seconds (mm:ss)",
  "props.formatSec": "Seconds (ss)",
  "props.continueAfterExpiry": "Allow answering after time expires",
  "props.autoSubmitOnExpiry": "Auto-submit when time expires",
  "props.buttonSettings": "Button Settings",
  "props.buttonText": "Button Text",
  "props.showNumbers": "Numbering",
  "props.numberPosition": "Number Position",
  "props.numberColor": "Number Color",
  "props.numberBgColor": "Number Background",

  // ── Constants ─────────────────────────────────────────────
  "constants.section.basic": "Basic",
  "constants.section.formElements": "Form Elements",
  "constants.element.textBlock": "Text Block",
  "constants.element.image": "Image",
  "constants.element.audioFile": "Audio File",
  "constants.element.slideVideo": "Slide (Video)",
  "constants.element.mathEditor": "Math Editor (MathType)",
  "constants.element.table": "Table",
  "constants.element.shape": "Geometric Shape",
  "constants.element.countArea": "Click to Count",
  "constants.element.keypadOneToFive": "Keypad (1-5)",
  "constants.element.keypadZeroToNine": "Keypad (0-9)",
  "constants.element.keypadMath": "Keypad (Math)",
  "constants.element.playButton": "Play (Text-to-Speech)",
  "constants.element.cubeBar5": "Cube Bar (5)",
  "constants.element.cubeBar10": "Cube Bar (10)",
  "constants.element.cubeSource": "Cube Source",
  "constants.element.colorPalette": "Color Palette",
  "constants.element.multipleChoice": "Multiple Choice",
  "constants.element.draggableItem": "Draggable Item",
  "constants.element.targetArea": "Target Area",
  "constants.element.orderingArea": "Ordering Area",
  "constants.element.sourcePoint": "Source Point",
  "constants.element.targetPoint": "Target Point",
  "constants.element.matching2Pairs": "Matching 2 Pairs",
  "constants.element.matching3Pairs": "Matching 3 Pairs",
  "constants.element.matching4Pairs": "Matching 4 Pairs",
  "constants.element.fillInTheBlanks": "Fill in the Blanks",
  "constants.element.paintArea": "Paint Area",
  "constants.element.colorSwatch": "Color Swatch",
  "constants.element.pushToTalk": "Push to Talk",

  // ── Appearance defaults ───────────────────────────────────
  "appearance.image.alt": "Image",
  "appearance.audio.label": "Play audio file",
  "appearance.pushToTalk.instructionText": "Press and hold to speak, your voice will be converted to text.",
  "appearance.pushToTalk.idleButtonText": "Push to Talk",
  "appearance.pushToTalk.listeningButtonText": "Listening...",
  "appearance.pushToTalk.processingButtonText": "Converting to text...",
  "appearance.pushToTalk.transcriptionLabel": "Recorded Answer",
  "appearance.pushToTalk.transcriptionPlaceholder": "Audio recording failed. Please type your answer here.",
  "appearance.pushToTalk.errorPreviewMessage": "Audio recording failed. Please check microphone permissions and browser settings.",
  "appearance.pushToTalk.oldPlaceholder": "Your audio recording will be automatically converted to text.",

  // ── Templates ─────────────────────────────────────────────
  "templates.text.defaultContent": "New text",
  "templates.choice.optionA": "Option A",
  "templates.choice.optionB": "Option B",
  "templates.choice.optionC": "Option C",
  "templates.choice.optionD": "Option D",
  "templates.dragSource.label": "Item",
  "templates.dropTarget.label": "Target Area",
  "templates.dropTarget.placeholder": "Empty area",
  "templates.orderList.label": "Ordering Area",
  "templates.orderList.placeholder": "Order the items",
  "templates.orderList.item1": "Item 1",
  "templates.orderList.item2": "Item 2",
  "templates.orderList.item3": "Item 3",

  // ── Validation ────────────────────────────────────────────
  "validation.feedback.perfect": "Perfect! You answered all questions correctly.",
  "validation.feedback.success": "Success!",
  "validation.feedback.fail": "You did not reach the passing score.",
  "validation.result.correct": "Correct!",
  "validation.result.partiallyCorrect": "Partially correct",
  "validation.result.wrong": "Wrong",
  "validation.canvas.noElements": "There must be at least one element on the canvas.",
  "validation.choice.noAnswerKey": "Answer key must be filled for choice questions. Please set the correct answers in the \"Answer Key\" section.",
  "validation.dragDrop.noAnswerKey": "Answer key must be filled for drag-and-drop target areas. Please set the accepted items in the \"Answer Key\" section.",
  "validation.orderList.noAnswerKey": "Answer key must be filled for ordering areas. Please set the correct order in the \"Answer Key\" section.",
  "validation.matching.noAnswerKey": "Answer key must be filled for matching questions. Please set the connections in the \"Answer Key\" section.",
  "validation.matching.noConnections": "At least one connection must be set for matching questions. Please set the connections in the \"Answer Key\" section.",
  "validation.fillInTheBlanks.noAnswerKey": "Answer key must be filled for fill-in-the-blanks questions. Please set the correct answers in the \"Answer Key\" section.",
  "validation.painting.noAnswerKey": "Answer key must be filled for painting questions. Please select a color for each paint area in the \"Answer Key\" section.",
  "validation.dragDrop.correctResult": "Correct!",
  "validation.dragDrop.wrongOrderAndItems": "Wrong order or items",
  "validation.dragDrop.wrongItems": "Wrong items",
  "validation.orderList.correct": "Correct! The order is completely correct.",
  "validation.orderList.wrongOrder": "Wrong order",

  // ── Renderers ─────────────────────────────────────────────
  "renderers.dropTarget.placeholder": "Empty area",
  "renderers.text.fallbackContent": "Text content",
  "renderers.image.addUrl": "Add image URL",
  "renderers.choice.imagePlaceholder": "Image",
  "renderers.audio.fallbackLabel": "Audio file",
  "renderers.audio.addUrl": "Add audio URL",
  "renderers.video.addUrl": "Add video URL",
  "renderers.dragSource.defaultLabel": "Item",
  "renderers.dropTarget.defaultLabel": "Target Area",
  "renderers.orderList.defaultLabel": "Ordering Area",
  "renderers.orderList.defaultPlaceholder": "Order the items",
  "renderers.dragSource.accessibleLabel": "Draggable item",
  "renderers.droppedItem.defaultLabel": "Item",
  "renderers.table.emptyCell": "Empty",
  "renderers.table.doubleClickToType": "double-click to type",
  "renderers.fillInTheBlanks.selectPlaceholder": "Select...",
  "renderers.pushToTalk.defaultErrorMessage": "Audio recording failed. Please type your answer.",
  "renderers.pushToTalk.stopButtonText": "Stop",
  "renderers.pushToTalk.startButtonText": "Start",
  "renderers.pushToTalk.stopTitle": "Click to stop",
  "renderers.pushToTalk.startTitle": "Click to speak",
  "renderers.pushToTalk.listeningAriaLabel": "Listening. Click to stop.",
  "renderers.pushToTalk.idleAriaLabel": "Click to speak.",
  "renderers.pushToTalk.canvasAriaLabel": "Push to talk (active in preview)",
  "renderers.pushToTalk.canvasErrorMessage": "Audio recording failed. Please check microphone permissions.",
  "renderers.pushToTalk.canvasTranscriptionLabel": "Recorded Answer",

  // ── Elements ──────────────────────────────────────────────
  "elements.label.textBlock": "Text Block",
  "elements.label.audioFile": "Audio File",
  "elements.label.colorPalette": "Color Palette",
  "elements.label.sourcePoint": "Source Point",
  "elements.label.targetPoint": "Target Point",

  // ── Canvas Screenshot ─────────────────────────────────────
  "canvasScreenshot.error.canvasNotFound": "Canvas element not found, screenshot could not be taken",
  "canvasScreenshot.error.previewNotFound": "Preview container not found, screenshot could not be taken",
  "canvasScreenshot.error.invalidBase64": "Invalid base64 format",

  // ── QuestionSettingsModal ─────────────────────────────────
  "questionSettings.title": "Question Settings",
  "questionSettings.subtitle": "Question metadata",
  "questionSettings.close": "Close",
  "questionSettings.questionType": "Question Type",
  "questionSettings.notSelected": "Not Selected",
  "questionSettings.subject": "Subject",
  "questionSettings.grade": "Grade",
  "questionSettings.topic": "Topic",
  "questionSettings.subtopic": "Subtopic",
  "questionSettings.objective": "Objective",
  "questionSettings.difficultyLevel": "Difficulty Level:",
  "questionSettings.solutionVideoUrl": "Solution Video (URL)",
  "questionSettings.lectureVideoUrl": "Lecture Video (URL)",
  "questionSettings.solutionTextUrl": "Solution Text (URL)",
  "questionSettings.timeLimit": "Time Limit (seconds)",
  "questionSettings.trackTime": "Track Time",
  "questionSettings.notMobileFriendly": "Not Mobile Friendly",
  "questionSettings.showInMbaExam": "Show in MBA exam",
  "questionSettings.cancel": "Cancel",
  "questionSettings.save": "Save",
  "questionSettings.preview": "Preview",
  "questionSettings.loading": "Loading...",
  "questionSettings.select": "Select",
  "questionSettings.selectSubjectFirst": "Select a subject first",
  "questionSettings.selectSubjectAndGradeFirst": "Select subject and grade first",
  "questionSettings.selectOptional": "Select (Optional)",
  "questionSettings.selectTopicFirst": "Select a topic first",
  "questionSettings.selectSubtopicFirst": "Select a subtopic first",
  "questionSettings.urlPreview": "URL preview",
  "questionSettings.openInNewTab": "Open in new tab",
  "questionSettings.videoPreview": "Video preview",
  "questionSettings.textPreview": "Text preview",

  // ── VideoLibraryModal ─────────────────────────────────────
  "videoLibrary.fallbackName": "Video File",
  "videoLibrary.loadError": "An error occurred while loading the video library",
  "videoLibrary.title": "Video Library",
  "videoLibrary.subtitle": "Select a video file",
  "videoLibrary.close": "Close",
  "videoLibrary.loading": "Loading...",
  "videoLibrary.empty": "Video library is empty",
  "videoLibrary.rootFolder": "Root Folder",
  "videoLibrary.folderEmpty": "This folder is empty",
  "videoLibrary.selected": "Selected",
  "videoLibrary.select": "Select",
  "videoLibrary.back": "← Back",
  "videoLibrary.cancel": "Cancel",

  // ── AnswerKeyModal ────────────────────────────────────────
  "answerKey.close": "Close",
  "answerKey.title": "Answer Key",
  "answerKey.subtitle": "Set correct answers for form elements",
  "answerKey.noFormElements": "There are no form elements on the canvas. Add form elements (choice, drag-and-drop, ordering, matching point, or paint area) first to set an answer key.",
  "answerKey.section.choiceQuestions": "Choice Questions",
  "answerKey.choice.multipleSelection": "Multiple Selection",
  "answerKey.choice.singleSelection": "Single Selection",
  "answerKey.choice.singleSelectionHint": "Only one correct answer can be selected in single selection.",
  "answerKey.section.fillInTheBlanks": "Fill in the Blanks Answers",
  "answerKey.fillInTheBlanks.label": "Fill in the Blanks",
  "answerKey.fillInTheBlanks.noAnswers": "No answers defined.",
  "answerKey.section.pushToTalk": "Push to Talk (Voice Response) Answers",
  "answerKey.pushToTalk.instruction": "Enter the accepted correct answers line by line. If the user's answer matches one of these texts, it may be counted as correct.",
  "answerKey.pushToTalk.noAnswers": "No correct answers added yet.",
  "answerKey.section.paintAreas": "Paint Areas",
  "answerKey.paintArea.noSwatches": "No color swatches found on the canvas. Please add color swatches first.",
  "answerKey.section.intersections": "Intersections",
  "answerKey.intersection.noSwatches": "No color swatches found on the canvas. Please add color swatches first.",
  "answerKey.section.dragDrop": "Drag & Drop Target Areas",
  "answerKey.dragDrop.existingItems": "Existing Items:",
  "answerKey.dragDrop.noItems": "No draggable items added yet.",
  "answerKey.dragDrop.acceptedItems": "Accepted Items",
  "answerKey.dragDrop.ordered": "(Ordered)",
  "answerKey.dragDrop.unordered": "(Unordered)",
  "answerKey.dragDrop.orderWarning": "Order matters - Drag to reorder",
  "answerKey.dragDrop.removeTitle": "Remove",
  "answerKey.dragDrop.constraints": "Constraints:",
  "answerKey.dragDrop.minItemCount": "Minimum Item Count:",
  "answerKey.dragDrop.maxItemCount": "Maximum Item Count:",
  "answerKey.dragDrop.exactCount": "Exact Count:",
  "answerKey.dragDrop.orderMatters": "Order Matters",
  "answerKey.dragDrop.exactCountNote": "Note: When \"Exact Count\" is set, min/max values are ignored.",
  "answerKey.section.matching": "Matching Connections",
  "answerKey.matching.noTargets": "No target points added yet. Add target points and create connections.",
  "answerKey.matching.sourcePoint": "Source Point",
  "answerKey.matching.availableTargets": "Available Target Points:",
  "answerKey.matching.connectedTargets": "Connected Target Points (",
  "answerKey.matching.removeConnectionTitle": "Remove Connection",
  "answerKey.section.orderList": "Order Lists",
  "answerKey.orderList.noItems": "No items added to this order list yet.",
  "answerKey.orderList.correctOrder": "Correct Order:",
  "answerKey.orderList.dragHint": "Drag to reorder",
  "answerKey.orderList.instruction": "Drag and drop items to set the correct order. Students will be evaluated based on this order.",
  "answerKey.cancel": "Cancel",
  "answerKey.save": "Save",
  "shape.star": "Star",
  "shape.circle": "Circle",
  "shape.square": "Square",
  "shape.triangle": "Triangle",
  "shape.heart": "Heart",
  "shape.diamond": "Diamond",
  "shape.pentagon": "Pentagon",
  "shape.hexagon": "Hexagon",
  "shape.octagon": "Octagon",
  "shape.oval": "Oval",
  "shape.trapezoid": "Trapezoid",
  "shape.parallelogram": "Parallelogram",
  "shape.cloud": "Cloud",
  "shape.flower": "Flower",
  "shape.sun": "Sun",

  // ── PreviewModal / FullScreenPreview ──────────────────────
  "previewModal.title": "Preview",
  "previewModal.close": "Close",
  "fullScreenPreview.title": "Preview",
  "fullScreenPreview.close": "Close",
  "fullScreenPreview.checkAnswer": "Check answer",
  "fullScreenPreview.allCorrect": "Well done! All answers are correct 🎉",
  "fullScreenPreview.summary": "{correct} / {total} correct",
  "fullScreenPreview.nothingToCheck": "There is nothing to check in this question.",
  "fullScreenPreview.resultCorrect": "Correct",
  "fullScreenPreview.resultWrong": "Wrong",
  "fullScreenPreview.resultEmpty": "Left empty",
  "fullScreenPreview.typeChoice": "Multiple choice",
  "fullScreenPreview.typeDragDrop": "Drag & drop",
  "fullScreenPreview.typeOrderList": "Ordering",
  "fullScreenPreview.typeMatching": "Matching",
  "fullScreenPreview.typeFillBlanks": "Fill in the blanks",
  "fullScreenPreview.typePaint": "Painting",

  // ── TemplateNameModal ─────────────────────────────────────
  "templateName.titleNew": "Save as New Template",
  "templateName.titleEdit": "Save as Template",
  "templateName.subtitle": "Enter a name for the template",
  "templateName.close": "Close",
  "templateName.nameLabel": "Template Name",
  "templateName.namePlaceholder": "e.g. Math Question Template",
  "templateName.tagsLabel": "Tags",
  "templateName.noTagsSelected": "No tags selected.",
  "templateName.searchPlaceholder": "Search or add tag",
  "templateName.noMatchingTags": "No matching tags found.",
  "templateName.tagHelp": "You can select multiple tags or add new ones.",
  "templateName.cancel": "Cancel",
  "templateName.save": "Save",

  // ── TemplatesModal ────────────────────────────────────────
  "templatesModal.fallbackName": "Unnamed Template",
  "templatesModal.title": "Templates",
  "templatesModal.subtitle": "Choose a template to get started quickly",
  "templatesModal.close": "Close",
  "templatesModal.searchPlaceholder": "Search templates or tags...",
  "templatesModal.noSearchResults": "No search results found",
  "templatesModal.noTemplates": "No templates found",
  "templatesModal.noTags": "No Tags",
  "templatesModal.altFallback": "Template",
  "templatesModal.cancel": "Cancel",

  // ── MediaLibraryModal ─────────────────────────────────────
  "mediaLibrary.fallbackName": "Image File",
  "mediaLibrary.loadError": "An error occurred while loading the media library",
  "mediaLibrary.title": "Media Library",
  "mediaLibrary.subtitle": "Select an image",
  "mediaLibrary.close": "Close",
  "mediaLibrary.loading": "Loading...",
  "mediaLibrary.empty": "Media library is empty",
  "mediaLibrary.rootFolder": "Root Folder",
  "mediaLibrary.folderEmpty": "This folder is empty",
  "mediaLibrary.back": "← Back",
  "mediaLibrary.cancel": "Cancel",

  // ── SoundLibraryModal ─────────────────────────────────────
  "soundLibrary.fallbackName": "Audio File",
  "soundLibrary.loadError": "An error occurred while loading the sound library",
  "soundLibrary.title": "Sound Library",
  "soundLibrary.subtitle": "Select an audio file",
  "soundLibrary.close": "Close",
  "soundLibrary.loading": "Loading...",
  "soundLibrary.empty": "Sound library is empty",
  "soundLibrary.rootFolder": "Root Folder",
  "soundLibrary.folderEmpty": "This folder is empty",
  "soundLibrary.stop": "Stop",
  "soundLibrary.play": "Play",
  "soundLibrary.back": "← Back",
  "soundLibrary.cancel": "Cancel",
  "localMedia.unsupported": "Drop or paste JPG, PNG, MP3, or MP4 files on the canvas.",
  "localMedia.partiallyUnsupported": "Some files were skipped. The canvas accepts JPG, PNG, MP3, and MP4 files.",
  "localMedia.uploading": "Saving scene media...",
  "localMedia.uploadError": "Scene media could not be saved.",
  "localMedia.missingUrlError": "Backend did not return a scene media URL.",

  // ── ImagePreviewModal ─────────────────────────────────────
  "imagePreview.title": "Image Preview",
  "imagePreview.close": "Close",
  "imagePreview.altFallback": "Image preview",
  "imagePreview.loadError": "Failed to load image",

  // ── ManageShortcutsModal ──────────────────────────────────
  "manageShortcuts.elementType.text": "Text",
  "manageShortcuts.elementType.image": "Image",
  "manageShortcuts.elementType.choice": "Choice",
  "manageShortcuts.elementType.audio": "Audio",
  "manageShortcuts.elementType.dragSource": "Draggable Item",
  "manageShortcuts.elementType.dropTarget": "Target Area",
  "manageShortcuts.elementType.mathtype": "Math (MathType)",
  "manageShortcuts.elementType.mathlive": "Math (Legacy)",
  "manageShortcuts.confirmClearAll": "Are you sure you want to delete all toolbar shortcuts?",
  "manageShortcuts.title": "Manage Toolbar Shortcuts",
  "manageShortcuts.close": "Close",
  "manageShortcuts.emptyMessage": "You haven't added any toolbar shortcuts yet.",
  "manageShortcuts.emptyHint": "You can add shortcuts by clicking the 📌 icons in the properties panel.",
  "manageShortcuts.clearAll": "Clear All",
  "manageShortcuts.colorPicker": "Color picker",
  "manageShortcuts.toggleControl": "Toggle",
  "manageShortcuts.remove": "Remove",

  // ── EducationalMaterialModal ──────────────────────────────
  "educationalMaterial.titleLectureVideos": "Lecture Videos",
  "educationalMaterial.titleSolutionTexts": "Solution Texts",
  "educationalMaterial.subtitleLectureVideo": "Select a lecture video",
  "educationalMaterial.subtitleSolutionText": "Select a solution text",
  "educationalMaterial.close": "Close",
  "educationalMaterial.searchVideoPlaceholder": "Search video title or URL...",
  "educationalMaterial.searchFilePlaceholder": "Search files...",
  "educationalMaterial.loading": "Loading...",
  "educationalMaterial.noSearchResults": "No search results found",
  "educationalMaterial.noLectureVideos": "No lecture videos found",
  "educationalMaterial.noSolutionTexts": "No solution texts found",
  "educationalMaterial.videosTab": "Videos",
  "educationalMaterial.filesTab": "Files",
  "educationalMaterial.cancel": "Cancel",

  // ── QuestionTypeChangeConfirmModal ────────────────────────
  "questionTypeChange.title": "Question Type Change",
  "questionTypeChange.irreversible": "This action cannot be undone",
  "questionTypeChange.changeInfo": "You are about to change the question type:",
  "questionTypeChange.currentType": "Current Type",
  "questionTypeChange.notSpecified": "Not Specified",
  "questionTypeChange.newType": "New Type",
  "questionTypeChange.warning": "Warning",
  "questionTypeChange.warningMessage": "This action will permanently delete all elements on the canvas and question type-related settings (answer key, etc.). This action cannot be undone.",
  "questionTypeChange.cancel": "Cancel",
  "questionTypeChange.confirmAndChange": "Confirm and Change",

  // ── FormControls ──────────────────────────────────────────
  "formControls.transparent": "Transparent",
  "formControls.border": "Border",
  "formControls.borderWidth": "Thickness",
  "formControls.color": "Color",
  "formControls.cornerRadius": "Corner",
  "formControls.padding": "Padding",
  "formControls.fontFamily": "Font Family",
  "formControls.fontSize": "Font Size",
  "formControls.textAlign": "Alignment",
  "formControls.alignLeft": "Left",
  "formControls.alignCenter": "Center",
  "formControls.alignRight": "Right",
  "formControls.alignJustify": "Justify",
  "formControls.fontWeight": "Weight",
  "formControls.fontWeightNormal": "Normal",
  "formControls.fontWeightMedium": "Medium",
  "formControls.fontWeightBold": "Bold",
  "formControls.fontWeightExtraBold": "Extra Bold",

  // ── ShortcutableControl ───────────────────────────────────
  "shortcutableControl.removeFromShortcuts": "Remove from Shortcuts",
  "shortcutableControl.addToToolbar": "Add to Toolbar",

  // ── LayersPanel ───────────────────────────────────────────
  "layersPanel.moveUp": "Move one layer up",
  "layersPanel.moveDown": "Move one layer down",

  // ── CanvasElement ─────────────────────────────────────────
  "canvasElement.fallbackContent": "Text content",

  // ── API ───────────────────────────────────────────────────
  "api.error.unknown": "An unknown error occurred",
  "api.media.allImages": "All Images",
  "api.media.other": "Other",
  "api.audio.allSounds": "All Sounds",

  // ── ElementPropertiesPanel extras ────────────────────────────
  "props.middleLeft": "Middle Left",
  "props.middleRight": "Middle Right",
  "props.bottomCenter": "Bottom Center",
  "props.headerBackgroundColor": "Header Background Color",
  "props.tableLock": "Lock Table (to edit elements inside)",
  "props.showOuterBorder": "Show Outer Border",
  "props.outerBorder": "Outer Border (Table)",
  "props.showCellBorders": "Show Cell Borders",
  "props.innerBorders": "Inner Borders (Cells)",
  "props.alternateRowColor": "Alternate Row Color",
  "props.dimensions": "Dimensions",
  "props.end": "End",
  "props.viewAppearance": "Appearance Settings",
  "props.noItemsAdded": "No items added yet",
  "props.showMoreItems": "more items to show",
  "props.maxUsage": "Maximum Usage (0 = unlimited)",
  "props.showLabelToggle": "Show Label",
  "props.labelText": "Label Text",
  "props.hoverEffect": "Hover Effect (Preview mode)",
  "props.hoverEffectDesc": "Grow and glow effect when hovering over source and target points",
  "props.allowMultipleConnections": "Allow Multiple Connections",
  "props.allowMultipleConnectionsDesc": "If enabled, this source point can connect to multiple target points",
  "props.sourcePointColors": "Source Point Colors",
  "props.connectionLine": "Connection Line Settings",
  "props.connectionThickness": "Connection Thickness (px)",
  "props.showNumbersToggle": "Show Numbering",
  "props.numPosition": "Number Position",
  "props.itemAppearance": "Item Appearance",
  "props.borderThicknessPx": "Border Thickness (px)",
  "props.cornerRoundnessPct": "Corner Roundness (%)",
  "props.frameSize": "Frame Size (px)",
  "props.maxItems": "Maximum Items (0 = unlimited)",
  "props.videoEndElements": "Elements to Show After Video Ends",
  "props.noOtherElements": "No other elements found.",
  "props.videoElementsHelp": "Selected elements will be hidden until the video ends, then shown.",
  "props.borderWidthLabel": "Border Width",
  "props.cornerRoundness": "Corner Roundness",
  "props.horizontalOuterMargin": "Horizontal Outer Margin",
  "props.verticalOuterMargin": "Vertical Outer Margin",
  "props.contentPx": "Content (px)",
  "props.waitingState": "Waiting",
  "props.listeningState": "Listening",
  "props.errorPlaceholderHelpNew": "This setting is now edited from the \"Text Area Placeholder\" field above when Canvas Status = Error.",
  "props.idAutoAssigned": "This ID was automatically assigned and cannot be changed",
  "props.paintAreaColorHelp": "Drag a color swatch to this area to paint it entirely.",
  "props.shapeDiamond": "Diamond",
  "props.shapeOctagon": "Octagon",
  "props.shapeOval": "Oval",
  "props.shapeTrapezoid": "Trapezoid",
  "props.shapeParallelogram": "Parallelogram",
  "props.shapeCloud": "Cloud",
  "props.addNewItem": "+ New Item",
  "props.sortArea": "Sort Area",
  "props.autoSizeHelpShort": "Leave blank for automatic sizing.",
  "props.imagePlaceholder": "Default",
  "props.optionContain": "Contain",
  "props.squareToRound": "0% = Square, 50% = Full Circle",
  "props.labelPosition": "Label Position",
  "props.pointSize": "Point Size",
  "props.pointSizePx": "Point Size (px)",
  "props.labelFontSize": "Label Font Size",
  "props.exampleStart": "e.g. Start",
  "props.exampleStop": "e.g. Stop",
  "props.elementsSelected": "elements selected",
  // ── MathTypeEditorModal ──────────────────────────────────
  "mathEditor.title": "Math Editor (MathType)",
  "mathEditor.expression": "Math Expression",
  "mathEditor.loading": "Loading...",
  "mathEditor.cancel": "Cancel",
  "mathEditor.save": "Save",

  "props.kenarlıkStili": "Border Style",
  "props.kenarlıkYuvarlak": "Border Roundness",
  "props.icBoslukYatay": "Padding (Horizontal)",
  "props.icBoslukDikey": "Padding (Vertical)",
  "props.icerikDuzeni": "Content Layout",
  "props.icerikHizalama": "Content Alignment",

  // ── SettingsModal ───────────────────────────────────────
  "settings.title": "Settings",
  "settings.subtitle": "Edit canvas and grid options here.",
  "settings.close": "Close",
  "settings.canvasOuterPadding": "Canvas Outer Padding",
  "settings.canvasInnerPadding": "Canvas Inner Padding",
  "settings.leftPx": "Left (px)",
  "settings.topPx": "Top (px)",
  "settings.rightPx": "Right (px)",
  "settings.bottomPx": "Bottom (px)",
  "settings.grid": "Grid",
  "settings.showGrid": "Show grid",
  "settings.cellSizePx": "Cell size (px)",
  "settings.backgroundSection": "Background",
  "settings.colorLabel": "Color",
  "settings.transparent": "Transparent",
  "settings.resetToDefaults": "Reset to Defaults",
  "settings.cancel": "Cancel",
  "settings.save": "Save",

  // ── SaveSuccessModal ────────────────────────────────────
  "saveSuccess.title": "Saved Successfully!",
  "saveSuccess.continueEditing": "Continue Editing",
  "saveSuccess.continueEditingDesc": "Continue editing the question",
  "saveSuccess.goToQuestions": "Go to Questions",
  "saveSuccess.goToQuestionsDesc": "Return to the question list",

  // ── RichTextEditor ──────────────────────────────────────
  "richTextEditor.hint": "Right-click or ⌘B/I/U",

  // ── QuestionTypeChangeConfirmModal extras ───────────────
  "questionTypeChange.elementCount": "There are {count} elements on the canvas.",

  // ── Validation extras ───────────────────────────────────
  "validation.score": "Your score: {score}/{maxScore} ({percentage}%)",
  "validation.correctCountSummary": "You answered {correctCount}/{totalCount} questions correctly.",
  "validation.dragDrop.exactCountRequired": "Exactly {count} items required (Current: {current})",
  "validation.dragDrop.minItemsRequired": "At least {count} items required (Current: {current})",
  "validation.dragDrop.maxItemsExceeded": "At most {count} items allowed (Current: {current})",
  "validation.orderList.wrongItemCount": "Wrong item count. Correct: {correct}, Yours: {current}",
  "validation.partialCreditSuffix": "points",
  "validation.partialPositionHint": "Some items are in the correct position",

  // ── QuestionSettingsModal extras ────────────────────────
  "questionSettings.timeLimitPlaceholder": "e.g. 120",

  // ── ErrorBoundary ───────────────────────────────────────
  "errorBoundary.title": "An error occurred",
};
